import axios from 'axios';
// import configService from './configService';

// API base URL - can be loaded from environment variables
//const API_URL = process.env.REACT_APP_API_URL || 'https://beeonixdec.bigtapp.net';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/';

// Create axios instance with common configuration
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 180000, // 3 minutes timeout for long-running operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get user ID from session storage
const getUserId = () => {
  try {
    const userInfo = sessionStorage.getItem('userInfo');
    if (userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      const userId = parsedUserInfo.email || parsedUserInfo.username;
      console.log('Retrieved user ID from session storage:', userId);
      return userId;
    }
  } catch (error) {
    console.error('Error getting user ID from session storage:', error);
  }
  console.log('No user ID found in session storage');
  return null;
};

// Helper function to get full user information from session storage
const getUserInfo = () => {
  try {
    const userInfo = sessionStorage.getItem('userInfo');
    if (userInfo) {
      return JSON.parse(userInfo);
    }
  } catch (error) {
    console.error('Error getting user info from session storage:', error);
  }
  return null;
};

// API endpoints
const api = {
  // Business Requirements Generator
  generateBusinessRequirement: (stakeholderInputs, userId = null) => {
    const currentUserId = userId || getUserId();
    return apiClient.post('/api/generate-business-requirement', {
      responses: stakeholderInputs,
      user_id: currentUserId,
    });
  },

  // Data Model Generator
  generateDataModel: (requirements, userId = null, modelName = '') => {
    const currentUserId = userId || getUserId();
    return apiClient.post('/api/generate-data-model', {
      requirements,
      user_id: currentUserId,
      model_name: modelName || null,  // Ensure we send null if no name provided
    });
  },

  // Convert Markdown to PDF
  convertMarkdownToPdf: (markdownText) => {
    return apiClient.post('/api/convert-markdown-to-pdf', {
      markdown_text: markdownText
    }, {
      responseType: 'blob' // Expect binary PDF response
    });
  },

  // Convert Markdown to Word
  convertMarkdownToWord: (markdownText) => {
    return apiClient.post('/api/convert-markdown-to-word', {
        markdown_text: markdownText
    }, {
        responseType: 'blob' // Expect binary DOCX response
    });
  },

  // ER Diagram Generator
  generateERDiagram: (dataModel) => {
    // This is a client-side implementation since the original used Azure OpenAI directly
    // We'll use the same system prompt as the original
    const systemPrompt = `You are a database modeling specialist who creates clean, well-formatted Entity-Relationship diagrams using Mermaid syntax. When presented with database schema information, transform it into precise Mermaid ER diagram code for standard relational databases.
 
## Guidelines for Creating Mermaid ER Diagrams:
 
1. Always start with \`erDiagram\` on the first line
 
2. Define entities and their attributes following this format:
   \`\`\`
   EntityName {
       DATA_TYPE attribute_name key_type(PK/FK/BLANK) "description"
   }
   \`\`\`
   
   - Use SIMPLE data types only (INTEGER, VARCHAR, TEXT, DATE, DECIMAL, etc.)
   - IMPORTANT: Do NOT include precision or length specifications like VARCHAR(255) or DECIMAL(10,2) - use only the base type name (VARCHAR, DECIMAL)
   - Always include descriptions in quotes
   - Mark primary keys with "PK" in the description
   - Mark foreign keys with "FK to TableName" in the description
 
3. Define relationships between entities using this format:
   \`\`\`
   EntityOne ||--o{ EntityTwo : "relationship description"
   \`\`\`
 
4. Use proper cardinality notation:
   - One-to-many: \`||--o{\`
   - One-to-one: \`||--||"
   - Many-to-many: \`}o--o{\`
   - Zero-or-one to many: \`|o--o{\`
   - One-to-zero-or-many: \`||--o{\`
   - Zero-or-one to zero-or-many: \`|o--o{\`
   - One-to-zero-or-one: \`||--|o\`
 
5. Use consistent naming conventions:
   - Use only entity name (sales) instead of dimensional modeling term (factsales)
   - Entity names should be singular nouns in PascalCase (e.g., Customer, Order, Product)
   - Avoid prefixes like "Dim" or "Fact" or any data warehouse specific terminology
   - Attribute names in snake_case (e.g., customer_id)
   - Relationship descriptions should be clear and concise verbs or phrases
 
6. Organize the code for readability:
   - List all entities first, then relationships
   - Group related entities together
   - Use proper indentation (4 spaces) for attributes
 
7. Ensure all entities have at least one primary key attribute
   
8. Include all necessary relationships to represent the data model accurately
 
Your output should be valid Mermaid syntax that can be rendered in Mermaid-compatible environments without any modifications. Remember that Mermaid has syntactical limitations and does not support complex data type specifications with precision or length parameters.`;

    return apiClient.post('/api/generate-mermaid', {
      data_model: dataModel,
      prompt_type: 'er_diagram',
      system_prompt: systemPrompt,
    });
  },

  // Dimensional Diagram Generator
  generateDimensionalDiagram: (dataModel) => {
    // This is a client-side implementation since the original used Azure OpenAI directly
    // We'll use the same system prompt as the original
    const systemPrompt = `You are a dimensional modeling assistant specializing in data warehouse design. Your task is to analyze the provided JSON data model and transform it into a well-formatted dimensional model (star schema or snowflake schema) represented as a Mermaid ER diagram.
 
When generating the Mermaid diagram:
1. Start with only 'erDiagram' on the first line
2. Identify appropriate fact and dimension tables based on the source entity relationships
3. Use clear naming conventions: FACT_SALES, DATE_DIM, CUSTOMER_DIM, etc.
4. For each table attribute, follow this exact format:
- \`attribute_name attribute_type key_type(PK/FK/BLANK)\`
- Example: \`sales_id BIGINT PK\`
- For foreign keys: \`date_id INTEGER FK\`
- For data types, use simple types: INTEGER, VARCHAR, DATE, DECIMAL, TEXT
5. For relationships use this exact format:
\`FACT_TABLE ||--o{ DIMENSION_TABLE : relationship_label\`
6. For star schema relationships (fact to dimension):
\`FACT_SALES ||--o{ DATE_DIM : "by_date"\`
7. For snowflake schema relationships (dimension to dimension):
\`BOOK_DIM ||--o{ AUTHOR_DIM : "written_by"\`
8. Use meaningful one-word relationship labels without quotes
9. Ensure clearly visible connections between related tables
10. Include a brief comment at the top identifying the schema type: "Star Schema" or "Snowflake Schema"

The diagram should show a properly structured dimensional model with a central fact table containing measures and foreign keys to descriptive dimension tables
Example of a valid table should be
FACT_TRANSACTION {
    transaction_id VARCHAR PK 
    account_id VARCHAR FK 
    date_id DATE FK 
    amount DECIMAL
    type VARCHAR 
    reference_id VARCHAR 
}`;

    return apiClient.post('/api/generate-mermaid', {
      data_model: dataModel,
      prompt_type: 'dimensional_diagram',
      system_prompt: systemPrompt,
    });
  },

  // SQL Generator
  generateSQL: (query, dialect, userId = null) => {
    const currentUserId = userId || getUserId();
    return apiClient.post('/api/generate-sql', {
      query,
      dialect,
      user_id: currentUserId,
    });
  },

  // ETL Mapping
  generateETLMapping: (sourceSchema, targetSchema, userId = null) => {
    const currentUserId = userId || getUserId();
    
    // Extract samples from schema objects if they exist
    const sourceSamples = sourceSchema.samples || null;
    const targetSamples = targetSchema.samples || null;
    
    // Remove samples from schema objects for API call
    const cleanSourceSchema = { ...sourceSchema };
    const cleanTargetSchema = { ...targetSchema };
    delete cleanSourceSchema.samples;
    delete cleanTargetSchema.samples;
    
    return apiClient.post('/api/etl-mapping', {
      source_schema: cleanSourceSchema,
      target_schema: cleanTargetSchema,
      source_samples: sourceSamples,
      target_samples: targetSamples,
      user_id: currentUserId,
    });
  },

  // Transformation Scripts Generator
  generateTransformationScripts: (requestData, userId = null) => {
    const currentUserId = userId || getUserId();
    
    return apiClient.post('/api/generate-transformation-scripts', {
      mapping_results: requestData.mapping_results,
      source_schema: requestData.source_schema,
      target_schema: requestData.target_schema,
      technology: requestData.technology,
      user_id: currentUserId,
    });
  },

  // Chat endpoints

  // --- Authentication ---
  loginUser: ({ username, email }) => {
    return apiClient.post('/auth/login', { username, email });
  },

  logoutUser: ({ username, email }) => {
    return apiClient.post('/auth/logout', { username, email });
  },

  // --- User Data Models for ETL ---
  fetchUserDataModels: (userId = null) => {
    const currentUserId = userId || getUserId();
    if (!currentUserId) {
      return Promise.reject(new Error('No user ID available'));
    }
    return apiClient.get(`/api/generate-data-model/user-data-models?user_id=${currentUserId}`);
  },

  postChatMessage: (endpoint, message, userId = null, followup = null) => {
    const currentUserId = userId || getUserId();
    const payload = { message, user_id: currentUserId };
    if (followup !== null) {
      payload.followup = Array.isArray(followup) ? followup.join(' ') : followup;
    }
    return apiClient.post(`/api/${endpoint}`, payload);
  },

  // Schema and History
  fetchSchema: (userId = null) => {
    const currentUserId = userId || getUserId();
    let params = {};
    
    if (currentUserId) {
      params.user_id = currentUserId;
      console.log(`Fetching schema for user: ${currentUserId}`);
    } else {
      console.warn('No user ID available for schema fetch');
    }
    
    return apiClient.get('/api/schema', { params });
  },

  fetchHistory: (endpoint, limit = 10, userId = null) => {
    const currentUserId = userId || getUserId();
    let params = { limit };
    
    if (currentUserId) {
      params.user_id = currentUserId;
      console.log(`Using user ID for history fetch: ${currentUserId}`);
    } else {
      console.warn('No user ID available for history fetch');
    }
    
    console.log(`Fetching history from endpoint: /${endpoint} with params:`, params);
    
    return apiClient.get(`/api/${endpoint}`, { params })
      .then(response => {
        console.log(`History response from ${endpoint}:`, response.data);
        return response;
      })
      .catch(error => {
        console.error(`Error fetching history from ${endpoint}:`, error);
        throw error;
      });
  },

  // User information helpers
  getCurrentUserId: () => getUserId(),
  getCurrentUserInfo: () => getUserInfo(),
  isUserAuthenticated: () => {
    const userInfo = getUserInfo();
    return userInfo !== null;
  },
  
  // Helper to get authenticated user ID with logging
  getAuthenticatedUserId: () => {
    const userId = getUserId();
    if (userId) {
      console.log('Retrieved authenticated user ID:', userId);
    } else {
      console.warn('No authenticated user ID found');
    }
    return userId;
  },

  // Upload source schema Excel file and get parsed schema
  uploadSourceSchemaFile: (formData) => {
    return axios.post(
      API_URL + 'api/parse-schema-file',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
  },
};

export default api; 
